import React, { useEffect, useState } from "react";
import Layout from "../components/Layout.jsx";
import { announcementService, employeeService } from "../services/apiService.jsx";
import "./Announcements.css";

const initialForm = {
  category: "employee_of_year",
  title: "",
  message: "",
  employee_id: "",
  announcement_date: new Date().toISOString().slice(0, 10),
  is_active: true,
};

const Announcements = () => {
  const [form, setForm] = useState(initialForm);
  const [announcements, setAnnouncements] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");

  const loadData = async () => {
    setLoading(true);
    try {
      const [announcementResponse, employeeResponse] = await Promise.all([
        announcementService.getAll(),
        employeeService.getAllEmployees(),
      ]);
      const announcementData = announcementResponse?.data?.data || announcementResponse?.data || [];
      const employeeData = employeeResponse?.data?.data || employeeResponse?.data || employeeResponse || [];
      setAnnouncements(Array.isArray(announcementData) ? announcementData : []);
      setEmployees(Array.isArray(employeeData) ? employeeData : []);
    } catch (error) {
      setNotice(error.response?.data?.message || "Could not load announcements");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((current) => ({ ...current, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setNotice("");
    try {
      await announcementService.create(form);
      setForm(initialForm);
      setNotice("Announcement published successfully.");
      await loadData();
    } catch (error) {
      setNotice(error.response?.data?.message || "Could not publish announcement");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this announcement?")) return;
    try {
      await announcementService.remove(id);
      setAnnouncements((current) => current.filter((item) => item.id !== id));
    } catch (error) {
      setNotice(error.response?.data?.message || "Could not delete announcement");
    }
  };

  return (
    <Layout>
      <main className="announcements-page">
        <div className="announcements-page__header">
          <div>
            <p className="announcements-page__eyebrow">Admin workspace</p>
            <h1>Announcements</h1>
            <p>Publish recognition, company updates, and important notices to the dashboard.</p>
          </div>
          <i className="bi bi-megaphone-fill" aria-hidden="true"></i>
        </div>

        {notice && <div className="announcements-page__notice">{notice}</div>}

        <section className="announcements-page__layout">
          <form className="announcements-form" onSubmit={handleSubmit}>
            <h2>Publish announcement</h2>
            <label>
              Type
              <select name="category" value={form.category} onChange={handleChange}>
                <option value="employee_of_year">Employee of the Year</option>
                <option value="employee_of_month">Employee of the Month</option>
                <option value="recognition">Recognition</option>
                <option value="general">General update</option>
              </select>
            </label>
            <label>
              Title
              <input name="title" value={form.title} onChange={handleChange} placeholder="Congratulations, Priya!" required />
            </label>
            <label>
              Employee (optional)
              <select name="employee_id" value={form.employee_id} onChange={handleChange}>
                <option value="">No employee selected</option>
                {employees.map((employee) => (
                  <option key={employee.employee_id} value={employee.employee_id}>
                    {employee.first_name} {employee.last_name} {employee.department_name ? `(${employee.department_name})` : ""}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Message
              <textarea name="message" value={form.message} onChange={handleChange} rows="5" placeholder="Add a short message for the team..." required />
            </label>
            <label>
              Publish date
              <input type="date" name="announcement_date" value={form.announcement_date} onChange={handleChange} required />
            </label>
            <label className="announcements-form__checkbox">
              <input type="checkbox" name="is_active" checked={form.is_active} onChange={handleChange} />
              Show this announcement on the dashboard
            </label>
            <button type="submit" disabled={saving}>
              <i className="bi bi-send-fill" aria-hidden="true"></i>
              {saving ? "Publishing..." : "Publish announcement"}
            </button>
          </form>

          <section className="announcements-list">
            <div className="announcements-list__header">
              <h2>Published announcements</h2>
              <span>{announcements.length}</span>
            </div>
            {loading ? <p className="announcements-list__empty">Loading announcements...</p> : announcements.length === 0 ? (
              <p className="announcements-list__empty">No announcements published yet.</p>
            ) : announcements.map((announcement) => (
              <article className="announcement-item" key={announcement.id}>
                <div>
                  <span className="announcement-item__category">{announcement.category.replaceAll("_", " ")}</span>
                  <h3>{announcement.title}</h3>
                  <p>{announcement.message}</p>
                  <small>{announcement.announcement_date?.slice(0, 10)}{announcement.first_name ? ` · ${announcement.first_name} ${announcement.last_name || ""}` : ""}</small>
                </div>
                <button className="announcement-item__delete" type="button" onClick={() => handleDelete(announcement.id)} title="Delete announcement" aria-label={`Delete ${announcement.title}`}>
                  <i className="bi bi-trash3" aria-hidden="true"></i>
                </button>
              </article>
            ))}
          </section>
        </section>
      </main>
    </Layout>
  );
};

export default Announcements;
